import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "tabler-icons-react";
import PageHeader from "components/PageHeader";
import { useSubmitQuizAnswersMutation } from "redux_dir/features/homeworkHelpApi";

// Helper pour convertir l'index en lettre (0 -> 'a', 1 -> 'b', 2 -> 'c')
const indexToLetter = (index) => String.fromCharCode(97 + index);

// Normaliser le texte pour comparaison (minuscule, trim, supprimer ponctuation finale)
const normalizeText = (text) => {
    if (!text) return '';
    return text.toLowerCase().trim().replace(/[.,;:!?]$/, '').trim();
};

function HomeworkQuizPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { quizData, eleveId } = location.state || {};
    // Stocker l'index de la proposition sélectionnée (0, 1, 2, etc.)
    const [userAnswers, setUserAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);
    const [submitQuizAnswers] = useSubmitQuizAnswersMutation();

    if (!quizData?.quiz) {
        return (
            <div className="flex flex-col h-[100dvh]">
                <PageHeader title="Quiz" path={`/${eleveId}/homework`} />
                <div className="flex-1 overflow-auto p-4">
                    <div className="text-center text-gray-500">
                        Aucune donnée de quiz disponible
                    </div>
                </div>
            </div>
        );
    }

    // Stocker l'index de la proposition sélectionnée
    const handleAnswerSelect = (questionIndex, propositionIndex) => {
        setUserAnswers(prev => ({
            ...prev,
            [questionIndex]: propositionIndex
        }));
    };

    // Trouver l'index de la bonne réponse dans les propositions
    const getCorrectIndex = (question) => {
        const correctAnswer = normalizeText(question.bonne_reponse);
        return question.propositions.findIndex(
            prop => normalizeText(prop) === correctAnswer
        );
    };

    // Vérifier si la réponse est correcte
    const isAnswerCorrect = (questionIndex) => {
        const question = quizData.quiz[questionIndex];
        const userAnswerIndex = userAnswers[questionIndex];
        const correctIndex = getCorrectIndex(question);
        return userAnswerIndex === correctIndex;
    };

    const calculateScore = () => {
        let correct = 0;
        quizData.quiz.forEach((question, index) => {
            if (isAnswerCorrect(index)) {
                correct++;
            }
        });

        return {
            correct,
            total: quizData.quiz.length,
            percentage: Math.round((correct / quizData.quiz.length) * 100)
        };
    };

    const getEncouragingMessage = (score) => {
        if (score === 100) return "🎉 Superstar! Tu as tout réussi! 🌟";
        if (score >= 80) return "👏 Excellent travail! Tu es très fort(e)! 💪";
        if (score >= 60) return "👍 Bien joué! Continue comme ça!";
        if (score >= 40) return "💫 Pas mal! Tu peux encore t'améliorer! 🌟";
        return "💝 Ne t'inquiète pas, on apprend de nos erreurs!";
    };

    return (
        <div className="flex flex-col h-full">
            <PageHeader title="Quiz" path={`/${eleveId}/homework`} />

            <div className="flex-1 overflow-auto bg-[rgba(0,0,0,0.03)]">
                <div className="p-4 space-y-4 max-w-2xl mx-auto">
                    {quizData.quiz.map((question, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg shadow">
                            <h3 className="font-bold mb-4">Question {index + 1}</h3>
                            <p className="mb-4">{question.question}</p>
                            <div className="space-y-2">
                                {question.propositions.map((proposition, propIndex) => {
                                    const userAnswerIndex = userAnswers[index];
                                    const correctIndex = getCorrectIndex(question);
                                    
                                    const isSelected = userAnswerIndex === propIndex;
                                    const isCorrectAnswer = propIndex === correctIndex;
                                    const isWrong = isSelected && propIndex !== correctIndex;

                                    let bgColor = '';
                                    if (showResults) {
                                        if (isCorrectAnswer) {
                                            bgColor = 'bg-[rgba(0,255,0,0.1)] border-[rgba(0,255,0,0.3)]';
                                        } else if (isWrong) {
                                            bgColor = 'bg-[rgba(255,0,0,0.1)] border-[rgba(255,0,0,0.3)]';
                                        }
                                    } else if (isSelected) {
                                        bgColor = 'bg-primary text-white';
                                    }

                                    return (
                                        <div
                                            key={propIndex}
                                            className={`p-3 border rounded-lg cursor-pointer transition-colors
                                                ${bgColor}
                                                ${!showResults && !isSelected ? 'hover:bg-[rgba(0,0,0,0.05)]' : ''}
                                            `}
                                            onClick={() => !showResults && handleAnswerSelect(index, propIndex)}
                                        >
                                            <span className="font-medium mr-2">{indexToLetter(propIndex)}.</span>
                                            {proposition}
                                            {showResults && isCorrectAnswer && (
                                                <span className="ml-2 text-green-600">✓</span>
                                            )}
                                            {showResults && isWrong && (
                                                <span className="ml-2 text-red-600">✗</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {showResults && (
                                <div className="mt-4 text-sm">
                                    {isAnswerCorrect(index) ? (
                                        <span className="text-green-600">✓ Bonne réponse</span>
                                    ) : (
                                        <span className="text-red-600">
                                            ✗ Mauvaise réponse. La bonne réponse était: {question.bonne_reponse?.toUpperCase()}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}

                    {!showResults ? (
                        <button
                            onClick={async () => {
                                setShowResults(true);

                                // Envoi des réponses au backend pour persister le détail
                                // de l'activité (question/réponse/note) - non bloquant
                                // pour l'UI qui utilise déjà le calcul local.
                                if (quizData?.quiz_session_id) {
                                    const answers = quizData.quiz.map((question, index) => ({
                                        question_id: question.question_id,
                                        reponse: question.propositions[userAnswers[index]],
                                    }));

                                    try {
                                        await submitQuizAnswers({
                                            quizSessionId: quizData.quiz_session_id,
                                            answers,
                                        }).unwrap();
                                    } catch (error) {
                                        console.error("Erreur lors de la soumission du quiz:", error);
                                    }
                                }
                            }}
                            disabled={Object.keys(userAnswers).length !== quizData.quiz.length}
                            className="w-full bg-primary text-white px-6 py-3 rounded-lg disabled:opacity-50 hover:bg-primary/90 transition-colors"
                        >
                            Valider les réponses
                        </button>
                    ) : (
                        <div className="bg-white p-4 rounded-lg shadow text-center">
                            <h3 className="font-bold mb-2">Résultats</h3>
                            <p className="text-lg">
                                Score: {calculateScore().correct}/{calculateScore().total}
                                ({calculateScore().percentage}%)
                            </p>
                            <p className="mt-2 text-gray-700">
                                {getEncouragingMessage(calculateScore().percentage)}
                            </p>
                            <div className="mt-4 space-y-3">
                                <button
                                    onClick={() => navigate(`/${eleveId}/homework`)}
                                    className="w-full bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
                                >
                                    Nouvelle aide aux devoirs
                                </button>
                                <button
                                    onClick={() => navigate("/parentProspect/select-enfant")}
                                    className="w-full bg-white text-primary border-2 border-primary px-6 py-3 rounded-lg hover:bg-primary/5 transition-colors"
                                >
                                    Retour à la liste des enfants
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default HomeworkQuizPage; 